import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { BRANDING } from '../config/branding';
import { RodapePublico } from '../components/RodapePublico';
import { t } from '../lib/i18n';
import { SimboloCeres } from '../components/Marca';
import { ApiError, api } from '../lib/api';
import { useSession } from '../lib/session';
import { useConfiguracaoPublica } from '../lib/configuracao-publica';
import { Field, InlineError, Spinner, useToast, FormErrorSummary } from '../components/ui';

export function LoginPage(): JSX.Element {
  const { login } = useSession();
  // CERES-EST-001 §11: a porta de entrada de quem ainda nao e cliente so
  // aparece onde o cadastro publico esta habilitado. A recusa de verdade e a
  // da API; isto aqui e para nao oferecer o que nao existe.
  const { config: configPublica } = useConfiguracaoPublica();
  const { notify } = useToast();
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  // `null` = nenhum pedido; senão, se o ambiente entrega e-mail de fato
  // (a resposta neutra muda de conteúdo, nunca de status — CERES-AUTH-REC-001).
  const [resetSent, setResetSent] = useState<null | { emailDeliveryEnabled: boolean }>(null);

  const handleSubmit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await login(email, password);
      const from = (location.state as { from?: string } | null)?.from;
      navigate(from ?? '/', { replace: true });
    } catch (caught) {
      setError(caught);
    } finally {
      setBusy(false);
    }
  };

  const handleReset = async (): Promise<void> => {
    if (email.trim() === '') {
      notify('error', 'Informe o e-mail para continuar.');
      return;
    }
    try {
      const resposta = await api.post<{ emailDeliveryEnabled?: boolean } | undefined>(
        '/auth/password-reset/request',
        { email },
      );
      // A resposta é sempre a mesma, exista o e-mail ou não.
      setResetSent({ emailDeliveryEnabled: resposta?.emailDeliveryEnabled === true });
    } catch (caught) {
      if (caught instanceof ApiError) notify('error', caught.mensagemHumana);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-surface-sunken px-4 py-10">
      <div className="w-full max-w-sm">
        <div className="mb-6 flex flex-col items-center text-center">
          <SimboloCeres className="mb-3 h-11 w-11 text-brand-600" decorativa />
          <h1 className="text-xl font-semibold text-ink">{BRANDING.productName}</h1>
          <p className="mt-0.5 text-sm text-ink-muted">{BRANDING.tagline}</p>
        </div>

        <div className="panel px-5 py-5">
          <form onSubmit={(event) => void handleSubmit(event)} className="space-y-4" noValidate>
            <Field label={t('entrar.email')} htmlFor="email" required>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="username"
                required
                autoFocus
                className="field-input"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
            </Field>

            <Field label={t('entrar.senha')} htmlFor="password" required>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                className="field-input"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />
            </Field>

            <FormErrorSummary error={error} />
            <InlineError error={error} />

            {resetSent !== null && (
              <p
                role="status"
                className="rounded border border-info-200 bg-info-50 px-3 py-2 text-sm text-info-700"
              >
                {resetSent.emailDeliveryEnabled
                  ? t('entrar.recuperacaoEnviada')
                  : t('entrar.recuperacaoTexto')}
              </p>
            )}

            <button type="submit" className="btn-primary w-full" disabled={busy}>
              {busy && <Spinner />}
              Entrar
            </button>

            <button
              type="button"
              className="btn-ghost btn-sm w-full"
              onClick={() => void handleReset()}
            >
              {t('entrar.esqueciSenha')}
            </button>
          </form>
        </div>

        {/* CERES-008b: a porta de entrada de quem ainda não é cliente.
            CERES-EST-001 §11: fechada enquanto não houver operação comercial. */}
        {configPublica.cadastroPublicoHabilitado && (
          <div className="mt-4 rounded-lg border border-line bg-surface px-5 py-4 text-center">
            <p className="text-sm text-ink-muted">{t('entrar.aindaNaoUsa')}</p>
            <Link to="/criar-conta" className="btn-secondary btn-sm mt-2 inline-flex">
              {t('entrar.criarConta')}
            </Link>
          </div>
        )}

        {/* As páginas públicas precisam ser ALCANÇÁVEIS: uma declaração de
            acessibilidade que só quem sabe o endereço encontra não serve a
            ninguém (§21.12). */}
        <RodapePublico />
        <p className="mt-1 text-center text-2xs text-ink-subtle">
          <Link to="/redefinir-senha" className="underline hover:text-ink-muted">
            {t('entrar.tenhoLink')}
          </Link>
        </p>
      </div>
    </div>
  );
}
